import { useFlag, useUpdateUser } from '@reflag/react-sdk';
import { useRecoilState, useSetRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { isLegacySiteType } from '@gitbook/app-pricing';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Switch, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { appReloadingMessageAtom } from '../../Application';
import {
    getOrganizationPlanBadgeProps,
    OrganizationIcon,
    SiteContentIcon,
    SiteTypeBadge,
} from '../../Components';
import { useNavigation } from '../../navigation';
import {
    apiResource,
    readAPICache,
    siteCentricOptInAtom,
    useAuthOperations,
    useLoggedInUser,
    userThemeAtom,
} from '../../state';
import { isOrgLevelScreen } from './ContentSidebar.utils';
import { transitionSiteCentric } from './SiteCentricTransition';

/**
 * Settings palette accessible from the sidebar header.
 */
export function useGetSettingsPalette(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const { organization, screen } = props;
    const { signOut } = useAuthOperations();
    const user = useLoggedInUser();
    const { navigate } = useNavigation();
    const [userTheme, setUserTheme] = useRecoilState(userThemeAtom);
    const setSiteCentricOptIn = useSetRecoilState(siteCentricOptInAtom);

    const isSiteCentricNavEnabled = useFlag('SITE_CENTRIC').isEnabled;
    const isSiteCentricToggleEnabled = useFlag('SITE_CENTRIC_TOGGLE').isEnabled;
    const updateUser = useUpdateUser();
    const setAppReloadingMessage = useSetRecoilState(appReloadingMessageAtom);

    const getOrganizationListPaletteGroups = useGetOrganizationListPaletteGroups();
    return useEventCallback(() => {
        const groups: PaletteGroupDef[] = [];

        const switchTheme = (theme: UserTheme) => {
            setUserTheme(theme);
            analytics.logEvent({
                type: 'account_edit_theme',
                theme,
            });
        };

        if (isSiteCentricNavEnabled) {
            groups.push({
                key: 'general',
                commands: [
                    ...(!isOrgLevelScreen(screen)
                        ? [
                              {
                                  key: 'general.home',
                                  title: 'Home',
                                  icon: Icon.Home,
                                  trigger: () => {
                                      navigate({
                                          screen: {
                                              type: 'home',
                                              organization: organization.id,
                                          },
                                      });
                                  },
                              },
                          ]
                        : []),
                ],
            });
        }

        groups.push({
            key: 'org_settings',
            commands: [
                {
                    key: 'org_settings.profile',
                    title: 'Settings',
                    icon: Icon.Settings,
                    trigger: () => {
                        navigate({
                            screen: {
                                type: 'org_settings',
                                organization: organization.id,
                                tab: { type: 'profile' },
                            },
                        });
                    },
                },
                organization.permissions.admin
                    ? {
                          key: 'org_settings.invite',
                          title: 'Invite your team',
                          icon: Icon.UserAdd,
                          trigger: () => {
                              navigate({
                                  overlay: {
                                      type: 'org_invite',
                                      organization: organization.id,
                                  },
                              });
                          },
                      }
                    : null,
            ].filter((x) => x !== null),
        });

        groups.push({
            key: 'personal_settings',
            commands: [
                {
                    key: 'personal_settings.switch',
                    icon: Icon.Modes,
                    title: 'Theme',
                    descendant: () =>
                        createPalette({
                            static: true,
                            groups: [
                                {
                                    key: 'theme',
                                    commands: [
                                        {
                                            key: 'theme.default',
                                            title: 'System',
                                            icon: Icon.DeviceDesktop,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Default,
                                            trigger: () => switchTheme(UserTheme.Default),
                                        },
                                        {
                                            key: 'theme.light',
                                            title: 'Light',
                                            icon: Icon.ThemeLight,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Light,
                                            trigger: () => switchTheme(UserTheme.Light),
                                        },
                                        {
                                            key: 'theme.dark',
                                            title: 'Dark',
                                            icon: Icon.ThemeDark,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Dark,
                                            trigger: () => switchTheme(UserTheme.Dark),
                                        },
                                    ],
                                },
                            ],
                        }),
                },
            ],
        });

        groups.push({
            key: 'account',
            commands: [
                {
                    key: 'switch-organization',
                    icon: Icon.Organization,
                    title: 'Switch organization',
                    descendant: () =>
                        createPalette({
                            groups: getOrganizationListPaletteGroups({
                                organizationId: organization.id,
                            }),
                        }),
                },
                {
                    key: 'account.signout',
                    icon: Icon.Leave,
                    title: 'Sign out',
                    trigger: () => {
                        signOut();
                    },
                },
                ...(isSiteCentricToggleEnabled
                    ? [
                          {
                              key: 'site_centric_navigation.toggle',
                              title: 'Try our new navigation',
                              subtitle: 'Browse content the way your site is organized.',
                              truncate: false,
                              kind: 'outline',
                              size: 'large',
                              className: '[&_span.flex-1]:max-w-44 group/toggle-palette-command',
                              badge: { kind: 'new', label: 'NEW' },
                              hasIconSibling: false,
                              type: PaletteCommandType.CUSTOM,
                              enabled: isSiteCentricNavEnabled,
                              suffix: (
                                  <Switch
                                      className="pointer-events-none ml-2 shrink-0 group-hover/toggle-palette-command:[&:not([data-state])]:bg-neutral-ui-standout group-data-[focused]/toggle-palette-command:[&:not([data-state])]:bg-neutral-ui-standout"
                                      aria-label="Try our new navigation"
                                      value={isSiteCentricNavEnabled}
                                  />
                              ),
                              trigger: () => {
                                  void toast.catch(async () => {
                                      await transitionSiteCentric({
                                          navigate,
                                          organizationId: organization.id,
                                          reloadingMessage: isSiteCentricNavEnabled
                                              ? 'Restoring your previous experience'
                                              : 'Getting your new experience ready',
                                          setAppReloadingMessage,
                                          setSiteCentricOptIn,
                                          siteCentricOptIn: isSiteCentricNavEnabled
                                              ? 'false'
                                              : 'true',
                                          updateUser,
                                          onUpdated: () => {
                                              analytics.logEvent({
                                                  type: 'account_toggle_site_centric',
                                                  toggleStatusTo: !isSiteCentricNavEnabled,
                                                  userId: user.uid,
                                                  organization: organization.id,
                                              });
                                          },
                                      });
                                  }, 'Error while updating the new site-centric navigation');
                              },
                          } satisfies PaletteCommandDef,
                      ]
                    : []),
            ],
        });

        return createPalette({ groups });
    });
}

function useGetOrganizationListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback((props: { organizationId: string }): PaletteGroupDef[] => {
        const { organizationId } = props;
        return [
            {
                key: 'organizations',
                commands: () => {
                    const organizations = readAPICache(
                        apiResource('listOrganizationsForAuthenticatedUser', { limit: 100 })
                    );

                    return [
                        ...organizations.items.map((org) => {
                            return {
                                key: org.id,
                                title: org.title,
                                ...(org.id === organizationId
                                    ? {
                                          type: PaletteCommandType.RADIO as const,
                                          selected: true,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                                icon: <OrganizationIcon organization={org} />,
                                badge:
                                    getOrganizationPlanBadgeProps({
                                        plan: org.plan,
                                        blend: true,
                                    }) ?? undefined,
                                trigger: () => {
                                    navigate({
                                        screen: {
                                            type: 'home',
                                            organization: org.id,
                                        },
                                    });
                                },
                            };
                        }),
                        {
                            key: 'create-organization',
                            title: 'Create an organization',
                            icon: Icon.Organization,
                            trigger: () => {
                                analytics.logEvent({
                                    type: 'organization_open_create',
                                    reason: 'sidebar_menu',
                                });

                                navigate({
                                    overlay: {
                                        type: 'welcome',
                                        organizationType: 'business',
                                        hash: 'sidebar_menu',
                                    },
                                });
                            },
                        },
                    ];
                },
            },
        ];
    });
}

export function useGetSiteListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback(
        (props: {
            currentSiteId: string | null;
            organizationContent: api.OrganizationAllContent;
            organizationId: string;
            onCreateSite?: () => void;
        }): PaletteGroupDef[] => {
            const { currentSiteId, onCreateSite, organizationContent, organizationId } = props;
            return [
                {
                    key: 'sites',
                    commands: organizationContent.sites.map((entry) => {
                        const { site } = entry;
                        return {
                            key: site.id,
                            title: site.title,
                            ...(site.id === currentSiteId
                                ? {
                                      type: PaletteCommandType.RADIO as const,
                                      selected: true,
                                  }
                                : {
                                      selected: undefined,
                                  }),
                            icon: <SiteContentIcon site={site} />,
                            suffix: !isLegacySiteType(site.appliedType) ? (
                                <SiteTypeBadge
                                    type={site.type}
                                    appliedType={site.appliedType}
                                    size="xxsmall"
                                    weight="medium"
                                />
                            ) : undefined,
                            trigger: () => {
                                navigate({
                                    screen: {
                                        type: 'site',
                                        organization: organizationId,
                                        site: site.id,
                                    },
                                });
                            },
                        };
                    }),
                },
                ...(onCreateSite
                    ? [
                          {
                              key: 'create-site',
                              commands: [
                                  {
                                      key: 'create-site',
                                      title: 'Create a site',
                                      icon: Icon.Plus,
                                      trigger: onCreateSite,
                                  },
                              ],
                          },
                      ]
                    : []),
            ];
        }
    );
}
